import { showToast } from "@/utils/toastUtils";
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { AxiosError } from "axios";

function getStatusCode(error: unknown): number | undefined {
  return (error as AxiosError)?.response?.status;
}

function getErrorMessage(error: any) {
  return error?.response?.data?.display_message || "에러가 발생했습니다.";
}

export function useHandledQuery<TData, TError = unknown>({
  queryKey,
  queryFn,
  options,
  config = { suppressErrorToast: false },
}: {
  queryKey: any[];
  queryFn: () => Promise<TData>;
  options?: UseQueryOptions<TData, TError>;
  config?: { suppressErrorToast?: boolean };
}): UseQueryResult<TData, TError> {
  const queryResult = useQuery<TData, TError>({
    queryKey,
    queryFn,
    ...options,
  });

  useEffect(() => {
    if (
      queryResult.isError &&
      queryResult.error &&
      !config?.suppressErrorToast
    ) {
      const status = getStatusCode(queryResult.error);
      const message = getErrorMessage(queryResult.error);
      switch (status) {
        case 401:
          showToast({
            type: "warning",
            message,
            description: "다시 로그인해주세요.",
          });
          break;
        case 403:
          showToast({
            type: "warning",
            message,
            description: "권한이 없습니다.",
          });
          break;
        case 404:
          showToast({
            type: "error",
            message,
            description: "존재하지 않는 페이지입니다.",
          });
          break;
        case 500:
          showToast({
            type: "error",
            message,
            description: "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
          });
          break;
        default:
          showToast({
            type: "error",
            message,
            description: "에러가 발생했습니다.",
          });
          break;
      }
    }
  }, [queryResult.isError, queryResult.error, config?.suppressErrorToast]);

  return queryResult;
}
